type OrganizationSettingsProfile = {
    readonly user: {
        readonly admin?: boolean | null;
    };
    readonly org_member_profiles: Array<{
        readonly organization_id: string;
        readonly role?: string | null;
    }>;
};

// Org settings access. Admins and the standard `scheduler` role qualify; the
// more-restricted `restricted_scheduler` tier does not. This also gates
// visibility of other members' roles (restricted schedulers can't see them).
export function canManageOrgSettings(
    userProfile: OrganizationSettingsProfile | null | undefined,
    organizationId: string | null | undefined,
): boolean {
    const currentOrgProfile = userProfile?.org_member_profiles.find(
        (profile) => profile.organization_id === organizationId,
    );

    return (
        userProfile?.user.admin === true ||
        currentOrgProfile?.role === 'admin' ||
        currentOrgProfile?.role === 'scheduler'
    );
}

// True admin (platform admin or org admin) — gates member management actions
// like inviting, changing roles, and removing members, which stay admin-only
// on the backend. Schedulers can manage org settings but not administer
// members.
export function isOrganizationAdmin(
    userProfile: OrganizationSettingsProfile | null | undefined,
    organizationId: string | null | undefined,
): boolean {
    const currentOrgProfile = userProfile?.org_member_profiles.find(
        (profile) => profile.organization_id === organizationId,
    );

    return (
        userProfile?.user.admin === true || currentOrgProfile?.role === 'admin'
    );
}

export function orgMemberRoleLabel(role: string | null | undefined): string {
    switch (role) {
        case 'admin':
            return 'Admin';
        case 'scheduler':
            return 'Scheduler';
        case 'restricted_scheduler':
            return 'Restricted Scheduler';
        default:
            return 'No role';
    }
}

export function orgMemberRoleDescription(
    role: string | null | undefined,
): string | null {
    switch (role) {
        case 'admin':
            return 'Full access, including organization settings and team members.';
        case 'scheduler':
            return 'Scheduling and outreach, plus organization settings.';
        case 'restricted_scheduler':
            return 'Scheduling and outreach only. Can’t change organization settings or view members’ roles.';
        default:
            return null;
    }
}
